import { useForm } from '@nerdgeschoss/react-use-form-library';
import { camelize } from 'humps';
import { camelCase } from 'lodash';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { FormImageUpload } from '../../../../components/Form/FileUpload/FormImageUpload';
import { FormForm } from '../../../../components/Form/FormForm';
import { Label } from '../../../../components/Form/Label/Label';
import { FormMultiSelect } from '../../../../components/Form/Select/FormMultiSelect';
import { FormSelect } from '../../../../components/Form/Select/FormSelect';
import { Switcher } from '../../../../components/Form/Switcher/Switcher';
import { FormTextField } from '../../../../components/Form/TextField/FormTextField';
import { Grid } from '../../../../components/Grid/Grid';
import { Stack } from '../../../../components/Stack/Stack';
import GlobalProps from '../../../../components/globalProps';
import { t } from '../../../../locales/translate';
import { paymentMethods } from '../../../../model/payment';
import { SLUG_REGEX } from '../../../../model/regex';
import { Shop, addShop, updateShop } from '../../../../model/shop';
import { shopPath } from '../../../../routes';
import './ShopForm.css';
import { handleError } from '../../../../model/notification';
import { FormNumberField } from '../../../../components/Form/NumberField/FormNumberField';

interface Props extends GlobalProps {
  shop: Partial<Shop>;
  onClose: () => void;
  onSave?: () => void;
}

export function ShopForm({ shop, onClose, onSave }: Props): JSX.Element {
  const history = useHistory();
  const { model, changes, fields, valid, dirty, onSubmit, submissionStatus } = useForm<Partial<Shop>>({
    model: {
      ...shop,
      defaultLocale: shop.defaultLocale || 'de',
      paymentBlacklist: shop.paymentBlacklist ? shop.paymentBlacklist.map((payment) => camelCase(payment)) : [],
      logo: shop.logo,
    },
    onSubmit: async () => {
      if (shop.id) {
        await updateShop(shop.id, model);
        if (changes.slug !== undefined) {
          history.push(shopPath(changes.slug));
        }
      } else {
        await addShop(model);
      }
      onSave?.();
      onClose();
    },
    validations: {
      name: 'required',
      slug: SLUG_REGEX,
    },
    onSubmitError: (errors) => {
      handleError(errors);
    },
  });

  return (
    <div className="add-shop">
      <FormForm
        onSubmit={onSubmit}
        onClose={onClose}
        canSubmit={dirty && valid}
        loading={submissionStatus === 'submitting'}
      >
        <Stack>
          <FormTextField {...fields.name} label={t.model.user.name} />
          <FormTextField {...fields.slug} label={t.model.shop.slug} description={t.model.shop.slugFormat} />
          <FormSelect
            {...fields.defaultLocale}
            label={t.model.shop.defaultLocale}
            options={[
              { value: 'en', label: t.general.english },
              { value: 'de', label: t.general.german },
            ]}
          />
          <FormTextField {...fields.domain} label={t.model.shop.domain} />
          <FormMultiSelect
            {...fields.paymentBlacklist}
            label={t.model.shop.paymentBlacklist}
            options={paymentMethods.map((method) => ({
              id: camelize(method.id),
              label: method.name,
            }))}
          />
          <FormNumberField
            {...fields.freeShippingThreshold}
            name="freeShippingThreshold"
            label={t.model.shop.freeShippingThreshold}
            step={0.01}
            min={0}
          />
          <Grid>
            <Stack>
              <Label title={t.model.shop.waitingQueue} />
              <Switcher {...fields.waitingQueue} labels={[t.general.yes, t.general.no]} />
            </Stack>
            <Stack>
              <Label title={t.model.shop.siteMap} />
              <Switcher {...fields.sitemap} labels={[t.general.yes, t.general.no]} />
            </Stack>
            <Stack>
              <Label title={t.model.shop.orderMergingAllowed} />
              <Switcher {...fields.orderMergingAllowed} labels={[t.general.yes, t.general.no]} />
            </Stack>
          </Grid>
          <FormImageUpload {...fields.logo} label={t.model.shop.logo} />
        </Stack>
      </FormForm>
    </div>
  );
}
